import * as THREE from "three";
import GUI from "lil-gui";

import "./style.css";
import { app } from "./context";
import globals from "./globals";
import parameterValues from "./parameterValues";
import { simulationUniforms, displayUniforms } from "./uniforms";
import { simulationMaterial, displayMaterial } from "./materials";
import { setupRenderTargets } from "./renderTargets";
import {
  drawFirstFrame,
  injectObstacleMaskIntoSimulation,
  InitialTextureTypes,
} from "./firstFrame";
import { setupMouse } from "./mouse";
import { setupKeyboard } from "./keyboard";
import { setupRightPaneGui } from "./setupRightPaneGui";

let fpsLastT = performance.now();
let fpsFrames = 0;

function tickFps(now) {
  fpsFrames += 1;
  const dt = now - fpsLastT;
  if (dt < 500) return;
  const value = Math.round((fpsFrames * 1000) / dt);
  fpsFrames = 0;
  fpsLastT = now;
  const el = document.getElementById("fps");
  if (el) el.textContent = `${value} FPS`;
}

function setupEnvironment() {
  app.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  app.scene = new THREE.Scene();

  app.displayMesh = new THREE.Mesh(
    new THREE.PlaneGeometry(2, 2),
    displayMaterial,
  );
  app.scene.add(app.displayMesh);

  app.renderer = new THREE.WebGLRenderer({ preserveDrawingBuffer: true });
  // app.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  app.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  app.renderer.setSize(
    parameterValues.canvas.width,
    parameterValues.canvas.height,
  );

  app.canvas = app.renderer.domElement;
  document.getElementById("container").appendChild(app.canvas);

  if (parameterValues.canvas.isMaximized) {
    app.canvas.style.width = `${window.innerWidth}px`;
    app.canvas.style.height = `${window.innerHeight}px`;
  }

  window.addEventListener("resize", resetTextureSizes, false);
  resetTextureSizes();
}

function resetTextureSizes() {
  if (parameterValues.canvas.isMaximized) {
    app.canvas.style.width = `${window.innerWidth}px`;
    app.canvas.style.height = `${window.innerHeight}px`;
  }

  parameterValues.canvas.width =
    app.canvas.clientWidth * parameterValues.canvas.scale;
  parameterValues.canvas.height =
    app.canvas.clientHeight * parameterValues.canvas.scale;

  setupRenderTargets();

  simulationUniforms.resolution.value.set(
    parameterValues.canvas.width,
    parameterValues.canvas.height,
  );

  const bufferCanvas = document.querySelector("#buffer-canvas");
  bufferCanvas.width = parameterValues.canvas.width;
  bufferCanvas.height = parameterValues.canvas.height;

  app.renderer.setSize(
    parameterValues.canvas.width,
    parameterValues.canvas.height,
    false,
  );
  app.camera.updateProjectionMatrix();
}

function update(now = performance.now()) {
  tickFps(now);

  if (!globals.isPaused) {
    if (
      globals.pendingMaskInject &&
      simulationUniforms.mousePosition.value.x >= 0
    ) {
      if (parameterValues.mask.enabled) injectObstacleMaskIntoSimulation();
      globals.pendingMaskInject = false;
    }

    const { displayMesh, renderer, scene, camera, renderTargets } = app;

    displayMesh.material = simulationMaterial;

    for (let i = 0; i < globals.pingPongSteps; i++) {
      const nextRenderTargetIndex =
        globals.currentRenderTargetIndex === 0 ? 1 : 0;

      simulationUniforms.previousIterationTexture.value =
        renderTargets[globals.currentRenderTargetIndex].texture;
      renderer.setRenderTarget(renderTargets[nextRenderTargetIndex]);
      renderer.render(scene, camera);
      simulationUniforms.previousIterationTexture.value =
        renderTargets[nextRenderTargetIndex].texture;
      displayUniforms.textureToDisplay.value =
        renderTargets[nextRenderTargetIndex].texture;

      globals.currentRenderTargetIndex = nextRenderTargetIndex;
    }

    displayMesh.material = displayMaterial;

    renderer.setRenderTarget(null);
    renderer.render(scene, camera);
  }

  requestAnimationFrame(update);
}

/** Wait for web fonts so canvas 2D mask/text uses real faces, not fallbacks. */
async function ensureCanvasFonts() {
  if (!document.fonts?.load) {
    await (document.fonts?.ready ?? Promise.resolve());
    return;
  }
  await Promise.all([
    document.fonts.load("16px Gulax"),
    document.fonts.load("16px PicNic"),
    document.fonts.load('16px "Google Sans Code"'),
    document.fonts.load('16px "Syne Mono"'),
  ]).catch(() => {});
  await document.fonts.ready;
}

async function bootstrap() {
  await ensureCanvasFonts();
  setupEnvironment();
  drawFirstFrame(InitialTextureTypes.RANDOM_POINTS);
  setupMouse();
  setupKeyboard();
  setupRightPaneGui(new GUI({ title: "CONTROLS" }));
  update();
}

bootstrap();
